import { Flex, FlexProps, Text, VisuallyHidden } from "@chakra-ui/react";
import {
  DragDropContext,
  Draggable,
  DragStart,
  DragUpdate,
  Droppable,
  DropResult,
  ResponderProvided,
} from "@hello-pangea/dnd";
import React from "react";
import { useGlossary } from "src/hooks/useGlossary";
import { WEGLOT_SKIP_CLASS } from "src/plugins/weglot/constants";
import { sortList } from "src/services/formTemplate/sorter";
import { InteractiveItem } from "./InteractiveItem";

type Props<ITEM> = {
  sortable: boolean;
  droppableId: string;
  items: readonly ITEM[];
  toId: (item: Readonly<ITEM>) => string;
  render: (item: Readonly<ITEM>) => React.ReactNode;
  toLabel: (item: Readonly<ITEM>) => string;
  onSort: (items: readonly ITEM[]) => Promise<void>;
  helpTextId: string | undefined;
  deleteButton: {
    /** Resolves to whether the item was removed, so a cancelled removal leaves queued moves alone. */
    onClick: (item: Readonly<ITEM>) => boolean | Promise<boolean>;
    label: string;
    hide?: (item: Readonly<ITEM>) => boolean;
  };
} & { interactiveItemProps?: FlexProps };

export function InteractiveList<ITEM>({
  sortable,
  droppableId,
  items,
  toId,
  render,
  toLabel,
  onSort,
  helpTextId,
  deleteButton,
  interactiveItemProps,
}: Props<ITEM>) {
  const [draggedIndex, setDraggedIndex] = React.useState<number | undefined>();
  const [itemsCache, setItemsCache] = React.useState<readonly ITEM[] | null>(
    null
  );
  const [announcement, setAnnouncement] = React.useState<React.ReactNode>(null);
  // the latest order the user asked for, which may be ahead of what has been saved.
  const pendingOrderRef = React.useRef<readonly ITEM[] | null>(null);
  // the order currently being saved.
  const savingOrderRef = React.useRef<readonly ITEM[] | null>(null);
  const isSavingRef = React.useRef(false);
  const previousItemsRef = React.useRef(items);
  const { glossary } = useGlossary();

  React.useEffect(() => {
    if (!announcement) return;
    // clear the live region so stale positions aren't left behind for screen readers.
    const timeout = setTimeout(() => setAnnouncement(null), 5000);
    return () => clearTimeout(timeout);
  }, [announcement]);

  React.useEffect(() => {
    // onSort saves the full list, so apply items added or removed outside the list to the pending order.
    // Otherwise a queued save would undo them.
    const previousIds = new Set(previousItemsRef.current.map(toId));
    const currentIds = new Set(items.map(toId));
    previousItemsRef.current = items;
    const pendingOrder = pendingOrderRef.current;
    if (pendingOrder === null) return;

    const pendingIds = new Set(pendingOrder.map(toId));
    const added = items.filter(
      (item) => !previousIds.has(toId(item)) && !pendingIds.has(toId(item))
    );
    const kept = pendingOrder.filter(
      (item) => !previousIds.has(toId(item)) || currentIds.has(toId(item))
    );
    if (added.length === 0 && kept.length === pendingOrder.length) return;

    pendingOrderRef.current = [...kept, ...added];
    setItemsCache(pendingOrderRef.current);
  }, [items, toId]);

  /**
   * Saves the latest requested order. Only one save runs at a time; moves made while a save is in
   * flight update the pending order, which is saved once the current save finishes.
   */
  const savePendingOrder = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      while (
        pendingOrderRef.current !== null &&
        pendingOrderRef.current !== savingOrderRef.current
      ) {
        savingOrderRef.current = pendingOrderRef.current;
        await onSort(savingOrderRef.current);
      }
    } catch (error) {
      // onSort is responsible for notifying the user about the error.
      console.error(error);
      setAnnouncement(
        glossary`The new order could not be saved. The list has been reset.`
      );
    } finally {
      pendingOrderRef.current = null;
      savingOrderRef.current = null;
      isSavingRef.current = false;
      setItemsCache(null);
    }
  };

  // onSort saves the full list, so a queued order that still had the removed item would add it back.
  const handleDelete = async (item: Readonly<ITEM>) => {
    if (!(await deleteButton.onClick(item))) return;
    const id = toId(item);
    const withoutItem = (order: readonly ITEM[]) =>
      order.filter((other) => toId(other) !== id);
    if (pendingOrderRef.current !== null) {
      pendingOrderRef.current = withoutItem(pendingOrderRef.current);
    }
    setItemsCache((cache) => cache && withoutItem(cache));
  };

  const reorder = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex === destinationIndex) return;
    const sortedItems = sortList(
      pendingOrderRef.current ?? items,
      sourceIndex,
      destinationIndex
    );
    pendingOrderRef.current = sortedItems;
    setItemsCache(sortedItems);
    void savePendingOrder();
  };

  /** Returns whether the item was moved. */
  const handleMove = (index: number, offset: -1 | 1) => {
    const currentItems = pendingOrderRef.current ?? items;
    const item = currentItems[index];
    const destinationIndex = index + offset;
    if (item === undefined || currentItems[destinationIndex] === undefined) {
      return false;
    }
    reorder(index, destinationIndex);
    // Weglot translates the announcement as a whole sentence, so keep the item name out of it and the rest in one text node.
    setAnnouncement(
      <>
        <span className={WEGLOT_SKIP_CLASS}>{toLabel(item)}</span>
        {` moved to position ${destinationIndex + 1} of ${currentItems.length}`}
      </>
    );
    return true;
  };

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    setDraggedIndex(undefined);

    if (result.destination === null) {
      return;
    }

    reorder(result.source.index, result.destination.index);
  };

  const handleDragUpdate = (update: DragUpdate) => {
    setDraggedIndex(update.source.index);
  };

  const handleDragStart = (start: DragStart) => {
    setDraggedIndex(start.source.index);
  };

  return (
    <DragDropContext
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
    >
      <Flex width="100%" paddingY={3} direction="column" flexGrow={1}>
        {sortable && (
          <Text
            alignSelf="center"
            textAlign="center"
            fontSize="xs"
            fontWeight="600"
            marginBottom={1}
          >
            {glossary`Rank 1 is the top choice. Drag or use the arrows to reorder.`}
          </Text>
        )}
        <Droppable droppableId={droppableId}>
          {(provided) => (
            <Flex
              ref={provided.innerRef}
              as={sortable ? "ol" : "ul"}
              // VoiceOver drops list semantics when the list style is removed, so set the role explicitly.
              role="list"
              listStyleType="none"
              width="100%"
              direction="column"
              {...provided.droppableProps}
            >
              {(itemsCache ?? items).map((item, index, currentItems) => {
                const id = toId(item);
                return (
                  <Draggable
                    draggableId={id}
                    index={index}
                    key={id}
                    isDragDisabled={!sortable}
                  >
                    {(provided, snapshot) => (
                      <InteractiveItem
                        item={item}
                        index={index}
                        itemCount={currentItems.length}
                        snapshot={snapshot}
                        provided={provided}
                        draggedIndex={draggedIndex}
                        render={render}
                        toLabel={toLabel}
                        onMove={(offset) => handleMove(index, offset)}
                        helpTextId={helpTextId}
                        deleteButton={{
                          ...deleteButton,
                          onClick: handleDelete,
                        }}
                        sortable={sortable}
                        {...interactiveItemProps}
                      />
                    )}
                  </Draggable>
                );
              })}

              <VisuallyHidden as="li" aria-hidden="true">
                {provided.placeholder}
              </VisuallyHidden>
            </Flex>
          )}
        </Droppable>
      </Flex>
      <VisuallyHidden role="status" aria-live="polite">
        {announcement}
      </VisuallyHidden>
    </DragDropContext>
  );
}
